import React, { useState, useEffect } from 'react';
import { X, Search, Send, UserPlus, Loader2, Trash2, Users, Link2, Copy, Check, AlertCircle, QrCode, ChevronRight } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE_URL = window.location.origin;

function ShareModal({ type, itemId, itemName, onClose }) {
  const [mode, setMode] = useState('choose'); // 'choose', 'link', 'email', 'qr'
  const [email, setEmail] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [collabData, setCollabData] = useState(null);
  const [loadingCollabs, setLoadingCollabs] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [recentCollabs, setRecentCollabs] = useState([]);
  const [showQR, setShowQR] = useState(false);
  
  // Share link state
  const [shareLink, setShareLink] = useState(null);
  const [loadingLink, setLoadingLink] = useState(false);
  const [copied, setCopied] = useState(false);
  const [revokingLink, setRevokingLink] = useState(false);

  useEffect(() => {
    if (type === 'folder' && itemId) {
      fetchCollaborators();
      fetchShareLink();
    } else if ((type === 'entry' || type === 'story') && itemId) {
      fetchShareLink();
    }
    loadRecentCollabs();
  }, [type, itemId]);

  const loadRecentCollabs = () => {
    try {
      const stored = localStorage.getItem('noyo_recent_collabs');
      if (stored) setRecentCollabs(JSON.parse(stored));
    } catch (e) {}
  };

  const saveRecentCollab = (email) => {
    try {
      const stored = localStorage.getItem('noyo_recent_collabs');
      let list = stored ? JSON.parse(stored) : [];
      list = [email, ...list.filter(e => e !== email)].slice(0, 10);
      localStorage.setItem('noyo_recent_collabs', JSON.stringify(list));
      setRecentCollabs(list);
    } catch (e) {}
  };

  const fetchCollaborators = async () => {
    setLoadingCollabs(true);
    try {
      const res = await axios.get(`${API}/folders/${itemId}/collaborators`);
      setCollabData(res.data);
    } catch (e) {
      setCollabData(null);
    } finally {
      setLoadingCollabs(false);
    }
  };

  const fetchShareLink = async () => {
    try {
      const endpoint = type === 'folder' ? `${API}/folders/${itemId}/share-link` : `${API}/stories/${itemId}/share-link`;
      const res = await axios.get(endpoint);
      if (res.data.has_link) {
        setShareLink(res.data);
      }
    } catch (e) {}
  };

  const createShareLink = async () => {
    setLoadingLink(true);
    setError('');
    try {
      const endpoint = type === 'folder' ? `${API}/folders/${itemId}/share-link` : `${API}/stories/${itemId}/share-link`;
      const res = await axios.post(endpoint);
      setShareLink({
        has_link: true,
        link_id: res.data.link_id,
        claimed_count: 0
      });
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to create link');
    } finally {
      setLoadingLink(false);
    }
  };

  const revokeShareLink = async () => {
    setRevokingLink(true);
    try {
      const endpoint = type === 'folder' ? `${API}/folders/${itemId}/share-link` : `${API}/stories/${itemId}/share-link`;
      await axios.delete(endpoint);
      setShareLink(null);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to revoke link');
    } finally {
      setRevokingLink(false);
    }
  };

  const copyLink = () => {
    const prefix = type === 'folder' ? 'invite' : 'story-invite';
    const url = `${BASE_URL}/${prefix}/${shareLink.link_id}`;
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSearch = async (q) => {
    setEmail(q);
    setError('');
    setSent(false);
    if (q.length < 3) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const res = await axios.get(`${API}/users/search?q=${encodeURIComponent(q)}`);
      setSearchResults(res.data);
    } catch (e) {
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const handleShare = async (targetEmail) => {
    setSending(true);
    setError('');
    try {
      if (type === 'folder') {
        await axios.post(`${API}/folders/${itemId}/share`, { email: targetEmail });
      } else if (type === 'story' || type === 'experience') {
        // Share a story/experience
        await axios.post(`${API}/stories/${itemId}/share`, { email: targetEmail });
      } else {
        // Share a journal entry
        await axios.post(`${API}/entries/${itemId}/share`, { email: targetEmail });
      }
      saveRecentCollab(targetEmail);
      setSent(true);
      setEmail('');
      setSearchResults([]);
      if (type === 'folder') fetchCollaborators();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to share');
    } finally {
      setSending(false);
    }
  };

  const handleRemove = async (shareId) => {
    setRemovingId(shareId);
    try {
      await axios.delete(`${API}/folders/share/${shareId}`);
      setConfirmRemove(null);
      fetchCollaborators();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to remove');
    } finally {
      setRemovingId(null);
    }
  };

  const acceptedCollabs = collabData?.collaborators?.filter(c => c.status === 'accepted') || [];
  const pendingCollabs = collabData?.collaborators?.filter(c => c.status === 'pending') || [];
  const isOwner = collabData?.is_owner ?? true;
  const currentEmails = new Set([...acceptedCollabs.map(c => c.collaborator_email), ...pendingCollabs.map(c => c.collaborator_email)]);
  const suggestedCollabs = recentCollabs.filter(e => !currentEmails.has(e));

  return (
    <div className="fixed inset-0 bg-black/60 z-[80] flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div className="bg-card-solid border border-border rounded-t-2xl sm:rounded-2xl shadow-xl w-full max-w-[100vw] sm:max-w-md p-4 sm:p-6 max-h-[80vh] overflow-y-auto overflow-x-hidden" onClick={e => e.stopPropagation()} data-testid="share-modal">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-primary" />
            <h3 className="text-lg font-serif font-semibold text-foreground">
              {type === 'folder' ? 'Manage Collaborators' : 'Share Story'}
            </h3>
          </div>
          <button onClick={onClose} className="p-1 text-muted-foreground hover:text-foreground" data-testid="share-modal-close"><X className="w-5 h-5" /></button>
        </div>

        {/* Current collaborators list */}
        {type === 'folder' && (
          <div className="mb-4">
            {loadingCollabs ? (
              <div className="flex items-center justify-center py-3"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>
            ) : (acceptedCollabs.length > 0 || pendingCollabs.length > 0) ? (
              <div className="space-y-1 mb-3">
                {acceptedCollabs.map(c => (
                  <div key={c.id} className="flex items-center justify-between px-3 py-2 bg-secondary/30 rounded-lg" data-testid={`collab-member-${c.id}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-medium text-primary">{(c.collaborator_email || '?')[0].toUpperCase()}</span>
                      </div>
                      <span className="text-sm text-foreground truncate">{c.collaborator_email}</span>
                    </div>
                    {isOwner && (
                      <button
                        onClick={() => setConfirmRemove(c)}
                        className="p-1 text-muted-foreground hover:text-red-500 transition-colors flex-shrink-0"
                        data-testid={`remove-collab-${c.id}`}
                        title="Remove collaborator"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                ))}
                {pendingCollabs.map(c => (
                  <div key={c.id} className="flex items-center justify-between px-3 py-2 bg-amber-50/50 border border-amber-200/30 rounded-lg" data-testid={`collab-pending-${c.id}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="w-7 h-7 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-medium text-amber-700">{(c.collaborator_email || '?')[0].toUpperCase()}</span>
                      </div>
                      <span className="text-sm text-muted-foreground truncate">{c.collaborator_email}</span>
                      <span className="text-[10px] text-amber-600 bg-amber-100 px-1.5 py-0.5 rounded-full font-medium flex-shrink-0">Pending</span>
                    </div>
                    {isOwner && (
                      <button
                        onClick={() => setConfirmRemove(c)}
                        className="p-1 text-muted-foreground hover:text-red-500 transition-colors flex-shrink-0"
                        data-testid={`remove-pending-${c.id}`}
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground mb-3">No collaborators yet</p>
            )}
            {!isOwner && (
              <p className="text-[11px] text-muted-foreground italic mb-2">Only the folder creator can remove members</p>
            )}
          </div>
        )}

        {/* QR Code section - collapsible, only for folders */}
        {type === 'folder' && (
          <div className="border-t border-border pt-4 mb-4">
            <button
              onClick={() => setShowQR(!showQR)}
              className="w-full flex items-center justify-between text-sm font-medium text-foreground mb-2"
              data-testid="toggle-qr-section"
            >
              <span className="flex items-center gap-1.5">
                <QrCode className="w-4 h-4 text-primary" />
                Share via QR Code
              </span>
              <ChevronRight className={`w-4 h-4 text-muted-foreground transition-transform ${showQR ? 'rotate-90' : ''}`} />
            </button>
            {showQR && (
              <div className="flex flex-col items-center py-3">
                <div className="bg-white p-4 rounded-xl shadow-sm border border-border" data-testid="qr-code-container">
                  <QRCodeSVG
                    value={`${BASE_URL}/join/${itemId}`}
                    size={160}
                    bgColor="#ffffff"
                    fgColor="#1a1a1a"
                    level="M"
                    includeMargin={false}
                  />
                </div>
                <p className="text-[11px] text-muted-foreground mt-2 text-center">Scan to instantly join this folder</p>
              </div>
            )}
          </div>
        )}

        {/* Share Link section */}
        <div className="border-t border-border pt-4 mb-4">
          <p className="text-sm font-medium text-foreground mb-3 flex items-center gap-1.5">
            <Link2 className="w-4 h-4 text-primary flex-shrink-0" />
            <span>Share via Link</span>
          </p>
            
            {shareLink ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2 bg-secondary/50 rounded-xl p-3 min-w-0">
                  <div className="flex-1 text-xs text-muted-foreground truncate font-mono overflow-hidden min-w-0" data-testid="share-link-url">
                    {`${BASE_URL}/${type === 'folder' ? 'invite' : 'story-invite'}/${shareLink.link_id}`}
                  </div>
                  <button
                    onClick={copyLink}
                    className="flex-shrink-0 p-2 rounded-lg bg-primary/10 hover:bg-primary/20 text-primary transition-colors"
                    data-testid="copy-share-link-btn"
                    title="Copy link"
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[11px] text-muted-foreground">
                    {shareLink.claimed_count || 0} {shareLink.claimed_count === 1 ? 'person' : 'people'} joined
                  </span>
                  <button
                    onClick={revokeShareLink}
                    disabled={revokingLink}
                    className="text-[11px] text-red-500 hover:text-red-600 transition-colors disabled:opacity-50"
                    data-testid="revoke-share-link-btn"
                  >
                    {revokingLink ? 'Revoking...' : 'Revoke Link'}
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={createShareLink}
                disabled={loadingLink}
                className="w-full flex items-center justify-center gap-2 py-2.5 bg-secondary/50 hover:bg-secondary border border-border rounded-xl text-sm text-foreground transition-colors disabled:opacity-50"
                data-testid="create-share-link-btn"
              >
                {loadingLink ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Link2 className="w-4 h-4" />
                )}
                <span>{loadingLink ? 'Creating...' : 'Create Share Link'}</span>
              </button>
            )}
          </div>

        {/* Invite section */}
        <div className="border-t border-border pt-4">
          <p className="text-sm font-medium text-foreground mb-2 flex items-center gap-1.5">
            <UserPlus className="w-4 h-4 text-primary" />
            {type === 'folder' ? 'Add someone' : 'Share with'}
          </p>

          {sent && (
            <div className="mb-3 px-3 py-2 bg-green-50 border border-green-200/50 rounded-lg">
              <p className="text-xs text-green-700 font-medium">Invite sent! They'll need to accept before joining.</p>
            </div>
          )}

          {/* Suggested recent collaborators */}
          {suggestedCollabs.length > 0 && !email && (
            <div className="mb-3">
              <p className="text-[11px] text-muted-foreground mb-1.5">Recent</p>
              <div className="flex flex-wrap gap-1.5 overflow-hidden">
                {suggestedCollabs.slice(0, 5).map(e => (
                  <button
                    key={e}
                    onClick={() => handleShare(e)}
                    disabled={sending}
                    className="text-xs bg-secondary/50 text-foreground px-2.5 py-1.5 rounded-full hover:bg-primary/10 hover:text-primary transition-colors disabled:opacity-50 flex items-center gap-1 max-w-full"
                    data-testid={`suggested-collab-${e}`}
                  >
                    <span className="truncate max-w-[120px] sm:max-w-[140px]">{e}</span>
                    <Send className="w-3 h-3 flex-shrink-0" />
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              value={email}
              onChange={e => handleSearch(e.target.value)}
              placeholder="Search by email..."
              className="w-full pl-10 pr-4 py-2.5 bg-secondary/50 border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
              data-testid="share-email-input"
            />
            {searching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />}
          </div>

          {error && <p className="text-xs text-red-500 mb-2" data-testid="share-error">{error}</p>}

          {searchResults.length > 0 && (
            <div className="border border-border rounded-xl overflow-hidden mb-3" data-testid="share-search-results">
              {searchResults.map(u => (
                <button
                  key={u.user_id}
                  onClick={() => handleShare(u.email)}
                  disabled={sending}
                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-secondary/50 transition-colors border-b border-border last:border-0 disabled:opacity-50"
                  data-testid={`share-user-${u.user_id}`}
                >
                  <div className="text-left">
                    <p className="text-sm font-medium text-foreground">{u.name || 'NOYO User'}</p>
                    <p className="text-xs text-muted-foreground">{u.email}</p>
                  </div>
                  <Send className="w-4 h-4 text-primary flex-shrink-0" />
                </button>
              ))}
            </div>
          )}

          {email.length >= 3 && searchResults.length === 0 && !searching && (
            <div className="text-center py-4">
              <p className="text-sm text-muted-foreground">No users found</p>
              <button
                onClick={() => handleShare(email)}
                disabled={sending || !email.includes('@')}
                className="mt-2 text-xs text-primary hover:underline disabled:opacity-50"
                data-testid="share-direct-email"
              >
                {sending ? 'Sending...' : `Try sharing with "${email}" directly`}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Confirm Remove Modal */}
      {confirmRemove && (
        <div className="fixed inset-0 bg-black/60 z-[90] flex items-center justify-center p-4" onClick={() => setConfirmRemove(null)}>
          <div className="bg-card-solid rounded-2xl p-6 max-w-sm w-full shadow-2xl" onClick={e => e.stopPropagation()} data-testid="confirm-remove-collab-modal">
            <h3 className="text-lg font-serif font-medium mb-2">Remove Collaborator?</h3>
            <p className="text-sm text-muted-foreground mb-5">Are you sure you want to remove <span className="font-medium text-foreground">{confirmRemove.collaborator_email}</span> from this folder? They will lose access to all shared stories.</p>
            <div className="flex justify-end space-x-3">
              <button onClick={() => setConfirmRemove(null)} className="bg-transparent text-foreground hover:bg-secondary rounded-full px-5 py-2 text-sm transition-colors" data-testid="cancel-remove-collab">Cancel</button>
              <button
                onClick={() => handleRemove(confirmRemove.id)}
                disabled={removingId === confirmRemove.id}
                className="bg-red-500 text-white hover:bg-red-600 rounded-full px-5 py-2 text-sm transition-colors disabled:opacity-50 flex items-center gap-1.5"
                data-testid="confirm-remove-collab"
              >
                {removingId === confirmRemove.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ShareModal;
